/* Focus rings for keyboard users only (24 Oct 2026). Pop-ups move focus to their close button and back to the button
 * that opened them, which keyboard and screen-reader users need; Safari then shows the focus ring even after a tap.
 * A tap or click marks the page with Wonder's no-focus-visible class, which hides every ring (custom-site-standards.css);
 * Tab or the arrow keys clear it. Two listeners, no scroll work. */
(() => {
  if (window.garrnishFocusRings) return;
  window.garrnishFocusRings = true;
  const KEYS = new Set(["Tab", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown"]);
  document.addEventListener("pointerdown", () => document.body.classList.add("no-focus-visible"), { capture: true, passive: true });
  document.addEventListener("keydown", (event) => {
    if (KEYS.has(event.key)) document.body.classList.remove("no-focus-visible");
  }, { capture: true, passive: true });
})();

/* Header behaviour (24 Oct 2026). The announcement and nav scroll away with the page; scrolling up brings the nav back.
 * Below 1200 px that holds everywhere; from 1200 px the nav stays pinned except in the purchase section. The offer bar
 * shows below 1200 px once the hero button has scrolled off the top of the screen (pages without the hero: after 75% of the
 * first screen) and steps aside over the purchase section, the buy bar and the footer. The same moment brings back the nav
 * button, which waits off screen until then (body class garrnish-hero-cta-gone; custom-site-standards.css, Header part; 28 Oct 2026).
 * Scrolling reads only scrollY: the purchase section and footer report through observers and the buy bar through its
 * event, heights are measured only on resize, and classes change only when a state flips, so scrolling never forces a layout.
 * A link into the purchase section (#offer) arrives with the nav away, even when the page scrolls up to get there, so the
 * nav never covers the top of the section; the visitor's own next scroll, tap or key hands control back. */
(() => {
  if (window.garrnishSiteHeader) return;
  const nav = document.querySelector(".page-header");
  const bar = nav?.querySelector(".wt-header");
  if (!bar) return;
  window.garrnishSiteHeader = true;

  const desktop = window.matchMedia("(min-width: 1200px)");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const TURN = 8; // px scrolled one way before the nav reacts, so small movements don't flicker it
  const PARK = 8; // extra px the away nav sits above the screen, so its shadow stays out of sight
  const purchase = new Set();
  let offer = document.querySelector(".garrnish-sticky-offer");
  let lastY = Math.max(0, window.scrollY);
  let turnY = lastY;
  let goingDown = false;
  let footerInView = false;
  let buyBar = false;
  let offerSelected = false;
  let navAway = false;
  let offerShown = false;
  let frame = 0;
  let jumping = false;
  let heroCta = document.querySelector(".presell-hero-v2__cta");
  let heroCtaGone = false;
  let announcementHeight = 0;
  let navHeight = 0;

  const measure = () => {
    announcementHeight = document.querySelector(".garrnish-announcement-section")?.offsetHeight || 0;
    navHeight = bar.offsetHeight;
  };

  // Where the nav sits on screen: pinned at the top, or (when away) in its place in the page, parked just above the screen.
  const navTop = (away, y) => Math.max(announcementHeight - y, away ? -(navHeight + PARK) : 0);

  const setNavAway = (away, y) => {
    const from = navTop(navAway, y) - navTop(away, y);
    nav.classList.toggle("garrnish-nav--away", (navAway = away));
    // The class moves the nav at once; this slides it from where it was. Transform only, so it runs off the main thread.
    if (from && !reduceMotion.matches) {
      bar.animate([{ transform: `translateY(${from}px)` }, { transform: "translateY(0)" }], {
        duration: 200,
        easing: "cubic-bezier(.22, 1, .36, 1)",
      });
    }
  };

  const setOfferShown = (shown) => {
    offer.classList.toggle("is-visible", (offerShown = shown));
    offer.setAttribute("aria-hidden", String(!shown));
    offer.querySelectorAll("a").forEach((link) => { link.tabIndex = shown ? 0 : -1; });
  };

  const render = () => {
    frame = 0;
    const y = Math.max(0, window.scrollY);
    const step = y - lastY;
    if (step && step > 0 !== goingDown) {
      goingDown = step > 0;
      turnY = lastY;
    }
    lastY = y;
    const inPurchase = purchase.size > 0;

    let away = navAway;
    if (y === 0 || document.body.classList.contains("menu-open")) away = false;
    else if (jumping) away = inPurchase || (!desktop.matches && navAway);
    // Keyboard focus in the nav keeps it on screen. After a tap or click (no-focus-visible) it doesn't: closing the cart or
    // menu hands focus back to its nav icon, which would otherwise pin the nav while the visitor scrolls on (28 Oct 2026).
    else if (nav.contains(document.activeElement) && !document.body.classList.contains("no-focus-visible")) away = false;
    else if (desktop.matches && !inPurchase) away = false;
    else if (Math.abs(y - turnY) > TURN) away = goingDown;
    if (away !== navAway) setNavAway(away, y);

    const showOffer = offerSelected ||
      (!desktop.matches && (heroCta ? heroCtaGone : y > window.innerHeight * 0.75) && !inPurchase && !buyBar && !footerInView);
    if (offer && showOffer !== offerShown) setOfferShown(showOffer);
  };
  const schedule = () => { frame ||= window.requestAnimationFrame(render); };

  const purchaseTarget = (hash) => {
    const id = hash.length > 1 ? decodeURIComponent(hash.slice(1)) : "";
    return Boolean(id && document.getElementById(id)?.matches(".gs-product"));
  };
  const jump = () => {
    jumping = true;
    schedule();
  };
  // Clicks reach here after the visitor's pointerdown or keydown, which end the previous jump, so a new one starts clean.
  document.addEventListener("click", (event) => {
    const link = event.target.closest?.("a[href*='#']");
    if (!link || link.origin !== location.origin || link.pathname !== location.pathname || !purchaseTarget(link.hash)) return;
    // Sections above the purchase section skip layout until they near the screen (custom-site-standards.css, Speed). Lay them
    // out before the browser scrolls, so the jump lands exactly instead of short by their estimated heights.
    document.documentElement.classList.add("g-show-sections");
    jump();
  });
  ["wheel", "touchstart", "pointerdown", "keydown"].forEach((type) => window.addEventListener(type, () => {
    if (!jumping) return;
    jumping = false;
    turnY = lastY;
  }, { capture: true, passive: true }));
  // Opening a page on #offer: the browser jumps there while the page is still loading, and content settling above can
  // leave it short. Until the visitor scrolls, taps or presses a key, line the section up again once loading finishes.
  if (purchaseTarget(location.hash)) {
    jump();
    const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    const settle = () => [300, 1200].forEach((delay) => window.setTimeout(() => {
      if (jumping && Math.abs(target.getBoundingClientRect().top - (parseFloat(getComputedStyle(target).scrollMarginTop) || 0)) > 2) target.scrollIntoView({ block: "start" });
    }, delay));
    if (document.readyState === "complete") settle();
    else window.addEventListener("load", settle, { once: true });
  }

  // A thin band across the middle of the screen: the purchase section "fills the screen" while it covers that band.
  const purchaseObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => (entry.isIntersecting ? purchase.add(entry.target) : purchase.delete(entry.target)));
    schedule();
  }, { rootMargin: "-45% 0px -45% 0px" });
  // Floating nav: light glass while the homepage hero photo is under the pills, stronger glass over cream pages.
  const overPhoto = bar.classList.contains("wt-header--floating") ? new IntersectionObserver((entries) => {
    bar.classList.toggle("garrnish-nav--over-photo", entries.some((entry) => entry.isIntersecting));
  }, { rootMargin: "0px 0px -90% 0px" }) : null;
  // The hero button counts as gone only once it has left through the top of the screen.
  const heroCtaObserver = new IntersectionObserver(([entry]) => {
    heroCtaGone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    document.body.classList.toggle("garrnish-hero-cta-gone", heroCtaGone);
    schedule();
  });
  const footerObserver = new IntersectionObserver((entries) => {
    footerInView = entries.some((entry) => entry.isIntersecting);
    schedule();
  });
  const observe = () => {
    purchaseObserver.disconnect();
    footerObserver.disconnect();
    heroCtaObserver.disconnect();
    purchase.clear();
    heroCta = document.querySelector(".presell-hero-v2__cta");
    if (heroCta) heroCtaObserver.observe(heroCta);
    if (overPhoto) {
      overPhoto.disconnect();
      document.querySelectorAll(".presell-hero-v2").forEach((hero) => overPhoto.observe(hero));
    }
    document.querySelectorAll(".gs-product").forEach((section) => purchaseObserver.observe(section));
    const footer = document.querySelector(".g-footer");
    if (footer) footerObserver.observe(footer);
  };
  const readBuyBar = () => {
    buyBar = Boolean(document.querySelector(".gss:not([hidden])"));
    schedule();
  };

  measure();
  observe();
  readBuyBar();
  window.addEventListener("scroll", schedule, { passive: true });
  if ("ResizeObserver" in window) {
    const resizeObserver = new ResizeObserver(() => { measure(); schedule(); });
    resizeObserver.observe(bar);
    const announcement = document.querySelector(".garrnish-announcement-section");
    if (announcement) resizeObserver.observe(announcement);
  }
  desktop.addEventListener("change", schedule);
  nav.addEventListener("focusin", schedule);
  document.addEventListener("garrnish:sticky-visibility", readBuyBar);

  if (window.Shopify?.designMode) {
    document.addEventListener("shopify:section:load", () => {
      offer = document.querySelector(".garrnish-sticky-offer");
      offerShown = false;
      measure();
      observe();
      schedule();
    });
    const select = (selected) => (event) => {
      if (!event.target.querySelector?.(".garrnish-sticky-offer")) return;
      offerSelected = selected;
      schedule();
    };
    document.addEventListener("shopify:section:select", select(true));
    document.addEventListener("shopify:section:deselect", select(false));
  }
})();
